/**
 * Data-provider для Refine, заточенный под наш бэк-контракт.
 *
 * Зачем кастом, а не голый `@refinedev/simple-rest`:
 * - наш бэк отдаёт `{items: [...], limit, offset}`, simple-rest ждёт массив;
 * - пагинация в API называется `?limit=&offset=`, simple-rest шлёт `?_start=&_end=`;
 * - фильтры формата `?status=enrolled`, без суффиксов `_eq`.
 *
 * Без этого refine `useList` для лидов / cohorts / payments возвращал
 * пустой набор — отсюда «Канбан 0 во всех колонках» при наличии данных в БД.
 */

import type { CrudFilter, CrudFilters, DataProvider } from "@refinedev/core";

import { api } from "../lib/api";

// VITE_API_URL = корень API ("https://api.accelerator.drafter.ru"). Все resource'ы
// админки живут под `/v1/admin/<resource>` — этот префикс собираем здесь,
// чтобы вызывающим (useList и т.п.) не нужно было знать о бэк-структуре.
const API_BASE: string = import.meta.env.VITE_API_URL || "/api";
const ADMIN_PREFIX = "/v1/admin";

function endpointFor(resource: string): string {
  return `${API_BASE}${ADMIN_PREFIX}/${resource}`;
}

interface ListEnvelope<T> {
  items: T[];
  limit?: number;
  offset?: number;
  total?: number;
}

/** `filters: [{field, operator, value}]` → query-params плоско.
 *  Поддерживаем только `eq` — этого хватает для всех текущих экранов
 *  (status / type / source). Остальное (gt/lt/contains) добавим по нужде. */
function flattenFilters(filters: CrudFilters | undefined): Record<string, string> {
  const params: Record<string, string> = {};
  if (!filters) return params;
  for (const f of filters as CrudFilter[]) {
    if (!("field" in f) || !("operator" in f)) continue;
    if (f.operator !== "eq") continue;
    const v = f.value;
    if (v === undefined || v === null || v === "") continue;
    params[f.field] = String(v);
  }
  return params;
}

export const dataProvider: DataProvider = {
  getApiUrl: () => API_BASE,

  getList: async ({ resource, pagination, filters }) => {
    const { current = 1, pageSize = 50 } = pagination ?? {};
    const params: Record<string, string | number> = {
      limit: pageSize,
      offset: (current - 1) * pageSize,
      ...flattenFilters(filters),
    };
    const resp = await api.get<ListEnvelope<unknown> | unknown[]>(endpointFor(resource), {
      params,
    });
    // Бэк всегда возвращает {items, limit, offset}; на всякий случай
    // поддержим и голый массив (для возможных endpoint'ов).
    const body = resp.data as ListEnvelope<unknown> | unknown[];
    if (Array.isArray(body)) {
      return { data: body as never, total: body.length };
    }
    const items = body.items ?? [];
    const total = body.total ?? items.length;
    return { data: items as never, total };
  },

  getOne: async ({ resource, id }) => {
    const resp = await api.get(`${endpointFor(resource)}/${id}`);
    return { data: resp.data };
  },

  create: async ({ resource, variables }) => {
    const resp = await api.post(endpointFor(resource), variables);
    return { data: resp.data };
  },

  update: async ({ resource, id, variables }) => {
    const resp = await api.patch(`${endpointFor(resource)}/${id}`, variables);
    return { data: resp.data };
  },

  deleteOne: async ({ resource, id }) => {
    const resp = await api.delete(`${endpointFor(resource)}/${id}`);
    return { data: resp.data };
  },

  custom: async ({ url, method, payload, query }) => {
    const config = { params: query };
    if (method === "get") {
      const resp = await api.get(url, config);
      return { data: resp.data };
    }
    if (method === "delete") {
      const resp = await api.delete(url, config);
      return { data: resp.data };
    }
    if (method === "post") {
      const resp = await api.post(url, payload, config);
      return { data: resp.data };
    }
    if (method === "put") {
      const resp = await api.put(url, payload, config);
      return { data: resp.data };
    }
    if (method === "patch") {
      const resp = await api.patch(url, payload, config);
      return { data: resp.data };
    }
    throw new Error(`Unsupported method ${method}`);
  },
};
